import { Injectable, signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { paymentInfo } from '../../types/paymentInfo';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth/auth.service';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { PopupService } from '../../core/../core/services/popup/popup.service';

@Injectable({
  providedIn: 'root',
})

export class PaymentService {
  // In-memory fallbacks
  payInfo: paymentInfo = {} as paymentInfo;
  docAmount: any = {};
  adtAmount: any = {};
  asstAmount: any = {};
  clipAmount: any = {};
  clipAdtAmount: any = {};
  routeInfo = '';

  // Keys for session storage persistence
  private readonly STORAGE_KEYS = {
    payInfo: 'payInfo',
    docAmount: 'docAmount',
    adtAmount: 'adtAmount',
    clipAmount: 'clipAmount',
    asstAmount: 'asstAmount',
    clipAdtAmount: 'clipAdtAmount',
    routeInfo: 'routeInfo',
  } as const;

  // Signals (reactive state) hydrated from sessionStorage if present
  private amountChosenSignal = signal<any>(
    this.restore<any>(this.STORAGE_KEYS.docAmount) || this.docAmount,
  );
  private assistanceAmountChosenSignal = signal<any>(
    this.restore<any>(this.STORAGE_KEYS.asstAmount) || this.asstAmount,
  );
  private adtAmountChosenSignal = signal<any>(
    this.restore<any>(this.STORAGE_KEYS.adtAmount) || this.adtAmount,
  );
  private clipAmountChosenSignal = signal<any>(
    this.restore<any>(this.STORAGE_KEYS.clipAmount) || this.clipAmount,
  );
  private clipAdtAmountChosenSignal = signal<any>(
    this.restore<any>(this.STORAGE_KEYS.clipAdtAmount) || this.clipAdtAmount,
  );
  private redirectCompSignal = signal<string>(
    this.restore<string>(this.STORAGE_KEYS.routeInfo) || this.routeInfo,
  );
  private paymentDetailsSignal = signal<paymentInfo>(
    this.restore<paymentInfo>(this.STORAGE_KEYS.payInfo) || this.payInfo,
  );

  // Public readonly signals for components to consume
  amountChosen = this.amountChosenSignal.asReadonly();
  assistanceAmountChosen = this.assistanceAmountChosenSignal.asReadonly();
  adtAmountChosen = this.adtAmountChosenSignal.asReadonly();
  clipAmountChosen = this.clipAmountChosenSignal.asReadonly();
  clipAdtAmountChosen = this.clipAdtAmountChosenSignal.asReadonly();
  redirectComp = this.redirectCompSignal.asReadonly();
  paymentDetails = this.paymentDetailsSignal.asReadonly();

  private sb_url = environment.do_services_sb_url;
  private paymentPageUrl = 'payment/loadPaymentPage';
  private insertInvoiceUrl = 'invoice/insertInvoice';
  private insertAdtInvoiceUrl = 'adt/buyAdtSubscriptionOffers';
  private insertClipInvoiceUrl = 'clip/buyClipSubscriptionOffers';
  private insertClipAdtInvoiceUrl = 'clipAdt/buyClipAdtSubscriptionOffers';
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(private http: HttpClient, private popupService: PopupService, 
              private authService: AuthService, private route: Router) { }

  // Generic restore helper
  private restore<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch (e) {
      return null;
    }
  }

  // Generic persist helper
  private persist(key: string, value: any) {
    try {
      if(value === undefined || value === null) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch (e) {
      // Ignore storage errors (e.g., quota, private mode)
    }
  }

  setAmountDetails(
    tokens: number,
    type: string,
    reprogId: number,
    lang: string,
    priceId: number,
    priceHTC: string,
    priceTTC: string,
    priceVAT: string,
    vatPert: string,
    userId: number,
    title: string,
  ) {
    this.docAmount = {
      tokenCount: tokens,
      type: type,
      reprogId: reprogId,
      modLanguage: lang,
      priceId: priceId,
      priceTTC: priceTTC,
      priceVAT: priceVAT,
      priceHTC: priceHTC,
      vatPercent: vatPert,
      userId: userId,
      title: title,
    };
    this.amountChosenSignal.set(this.docAmount);
    this.persist(this.STORAGE_KEYS.docAmount, this.docAmount);
  }

  setAdtAmountDetails(
    type: string,
    adtId: number,
    priceId: number,
    priceHTC: string,
    priceTTC: string,
    priceVAT: string,
    vatPert: string,
    userId: number,
    countryCode: string,
    lang: string,
    parentId: number,
    title: string,
  ) {
    this.adtAmount = {
      type: type,
      adtSubscriptionOfferId: adtId,
      priceId: priceId,
      priceTTC: priceTTC,
      priceVAT: priceVAT,
      priceHTC: priceHTC,
      vatPercent: vatPert,
      userId: userId,
      countryCode: countryCode,
      languageCode: lang,
      parentId: parentId,
      title: title,
    };
    this.adtAmountChosenSignal.set(this.adtAmount);
    this.persist(this.STORAGE_KEYS.adtAmount, this.adtAmount);
  }

  setClipAmountDetails(
    type: string,
    clipId: number,
    priceId: number,
    priceHTC: string,
    priceTTC: string,
    priceVAT: string,
    vatPert: string,
    userId: number,
    countryCode: string,
    lang: string,
    parentId: number,
    title: string,
  ) {
    this.clipAmount = {
      type: type,
      clipSubscriptionOfferId: clipId,
      priceId: priceId,
      priceTTC: priceTTC,
      priceVAT: priceVAT,
      priceHTC: priceHTC,
      vatPercent: vatPert,
      userId: userId,
      countryCode: countryCode,
      languageCode: lang,
      parentId: parentId,
      title: title,
    };
    this.clipAmountChosenSignal.set(this.clipAmount);
    this.persist(this.STORAGE_KEYS.clipAmount, this.clipAmount);
  }

  setClipAdtAmountDetails(
    type: string,
    clipAdtId: number,
    priceId: number,
    priceHTC: string,
    priceTTC: string,
    priceVAT: string,
    vatPert: string,
    userId: number,
    countryCode: string,
    lang: string,
    parentId: number,
    title: string,
  ) {
    this.clipAdtAmount = {
      type: type,
      clipAdtSubscriptionOfferId: clipAdtId,
      priceId: priceId,
      priceTTC: priceTTC,
      priceVAT: priceVAT,
      priceHTC: priceHTC,
      vatPercent: vatPert,
      userId: userId,
      countryCode: countryCode,
      languageCode: lang,
      parentId: parentId,
      title: title,
    };
    this.clipAdtAmountChosenSignal.set(this.clipAdtAmount);
    this.persist(this.STORAGE_KEYS.clipAdtAmount, this.clipAdtAmount);
  }

  setAssistanceAmountDetails(
    tokens: number,
    type: string,
    assistanceId: number,
    lang: string,
    priceId: number,
    priceHTC: string,
    priceTTC: string,
    priceVAT: string,
    vatPert: string,
    userId: number,
    title: string,
  ) {
    this.asstAmount = {
      assitanceTokenPurchasedQuantity: tokens,
      type: type,
      assistanceOfferId: assistanceId,
      modLanguage: lang,
      priceId: priceId,
      priceTTC: priceTTC,
      priceVAT: priceVAT,
      priceHTC: priceHTC,
      vatPercent: vatPert,
      userId: userId,
      title: title,
    };
    this.assistanceAmountChosenSignal.set(this.asstAmount);
    this.persist(this.STORAGE_KEYS.asstAmount, this.asstAmount);
  }

  setPaymentDetails(payDetails: paymentInfo) {
    this.payInfo = payDetails;
    this.paymentDetailsSignal.set(this.payInfo);
    this.persist(this.STORAGE_KEYS.payInfo, this.payInfo);
  }

  // Method to set redirect component
  setRedirectComp(route: string) {
    this.routeInfo = route;
    this.redirectCompSignal.set(this.routeInfo);
    this.persist(this.STORAGE_KEYS.routeInfo, this.routeInfo);
  }

  // Method to clear amount details (for cleanup)
  clearAmountDetails() {
    this.payInfo = {} as paymentInfo;
    this.docAmount = {};
    this.adtAmount = {};
    this.asstAmount = {};
    this.clipAmount = {};
    this.clipAdtAmount = {};
    this.routeInfo = '';
    this.amountChosenSignal.set(this.docAmount);
    this.adtAmountChosenSignal.set(this.adtAmount);
    this.clipAmountChosenSignal.set(this.clipAmount);
    this.clipAdtAmountChosenSignal.set(this.clipAdtAmount);
    this.assistanceAmountChosenSignal.set(this.asstAmount);
    this.redirectCompSignal.set(this.routeInfo);
    this.paymentDetailsSignal.set(this.payInfo);
    // Clear persisted storage
    Object.values(this.STORAGE_KEYS).forEach((key) =>
      sessionStorage.removeItem(key),
    );
  }

  getPaymentPage(purchaseData: string): Observable<any> {
    const options = {
      headers: this.headers,
    };
    return this.http.post(this.sb_url + this.paymentPageUrl, purchaseData, options).pipe(
        map((response: any) => {
          const run = response;
          return run;
        }),
        catchError((e: any) => this.handleError(e)),
      );
  }

  insertInvoice(invoiceInfo: string): Observable<any> {
    const options = {
      headers: this.headers,
    };
    return this.http.post(this.sb_url + this.insertInvoiceUrl, invoiceInfo, options).pipe(
        map((response: any) => {
          const run = response;
          return run;
        }),
        catchError((e: any) => this.handleError(e)),
      );
  }

  insertAdtInvoice(invoiceInfo: string): Observable<any> {
    const options = {
      headers: this.headers,
    };
    return this.http.post(this.sb_url + this.insertAdtInvoiceUrl, invoiceInfo, options).pipe(
        map((response: any) => {
          const run = response;
          return run;
        }),
        catchError((e: any) => this.handleError(e)),
      );
  }

  insertClipInvoice(invoiceInfo: string): Observable<any> {
    const options = {
      headers: this.headers,
    };
    return this.http.post(this.sb_url + this.insertClipInvoiceUrl, invoiceInfo, options).pipe(
        map((response: any) => {
          const run = response;
          return run;
        }),
        catchError((e: any) => this.handleError(e)),
      );
  }

  insertClipAdtInvoice(invoiceInfo: string): Observable<any> {
    const options = {
      headers: this.headers,
    };
    return this.http.post(this.sb_url + this.insertClipAdtInvoiceUrl, invoiceInfo, options).pipe(
        map((response: any) => {
          const run = response;
          return run;
        }),
        catchError((e: any) => this.handleError(e)),
      );
  }

  private handleError(error: any): Observable<never> {
    if([400, 403, 404, 405, 500, 502, 503, 504].includes(error.status)) {
      return throwError(() => error);
    }
    if(error.status === 401) {
      const dialogRef = this.popupService.openDialog(MessagePopupComponent, {
        data: { message: 'Unauthorized Request - Your Session has expired' },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.authService.logout().subscribe(() => {
          this.route.navigate(['/home']);
        });
      });
      return throwError(
        () => new Error('Unauthorized Request - Session expired'),
      );
    }
    return throwError(
      () => new Error(`Unhandled status code: ${error.status}`),
    );
  }
}
